import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

const ProductDetail = ({ product, campaigns, metrics, onStartAddCampaign, onRemoveCampaign }) => {
  const [activeTab, setActiveTab] = useState('overview'); // overview, campaigns, metrics
  
  // Formatar a data para exibição
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };
  
  // Obter a tradução do status para exibição
  const getStatusLabel = (statusValue) => {
    const statuses = {
      'active': 'Ativo',
      'inactive': 'Inativo',
      'development': 'Em Desenvolvimento',
      'discontinued': 'Descontinuado'
    };
    
    return statuses[statusValue] || statusValue;
  };
  
  // Obter a tradução da categoria para exibição
  const getCategoryLabel = (categoryValue) => {
    const categories = {
      'software': 'Software',
      'curso': 'Curso',
      'consultoria': 'Consultoria',
      'servico': 'Serviço',
      'outro': 'Outro'
    };
    
    return categories[categoryValue] || categoryValue;
  };
  
  // Dados para gráfico de investimento x receita
  const investmentRevenueData = campaigns.map(campaign => ({
    date: new Date(campaign.date).toLocaleDateString('pt-BR'),
    investment: parseFloat(campaign.investment),
    revenue: parseFloat(campaign.revenue),
    profit: parseFloat(campaign.revenue) - parseFloat(campaign.investment)
  }));
  
  // Dados para gráfico de vendas por tempo
  const salesData = campaigns.map(campaign => ({
    date: new Date(campaign.date).toLocaleDateString('pt-BR'),
    sales: parseInt(campaign.sales),
    leads: parseInt(campaign.leads)
  }));
  
  // Dados para gráfico de distribuição de investimento por canal
  const channelData = [];
  const channelMap = {};
  
  campaigns.forEach(campaign => {
    const channel = campaign.channel;
    if (!channelMap[channel]) {
      channelMap[channel] = 0;
    }
    channelMap[channel] += parseFloat(campaign.investment);
  });
  
  Object.entries(channelMap).forEach(([channel, investment]) => {
    channelData.push({
      name: channel,
      value: investment
    });
  });
  
  // Cores para os canais
  const CHANNEL_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
  
  // Formatação personalizada para o tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{`${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.name}: ${entry.name.includes('ROI') ? entry.value.toFixed(2) + '%' : 
                entry.name.includes('R$') ? 'R$ ' + entry.value.toFixed(2) : 
                entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="product-detail">
      <div className="product-detail-header">
        <div className="product-detail-info">
          <h2>{product.name}</h2>
          <div className="product-meta">
            <span className={`product-status status-${product.status}`}>
              {getStatusLabel(product.status)}
            </span>
            <span className="product-category">{getCategoryLabel(product.category)}</span>
            <span className="product-price">R$ {parseFloat(product.price).toFixed(2)}</span>
            <span className="product-date">Lançado em: {formatDate(product.launchDate)}</span>
          </div>
          
          {product.description && (
            <div className="product-description">
              <p>{product.description}</p>
            </div>
          )}
        </div>
        
        <div className="product-detail-metrics">
          <div className="metric-item">
            <span className="metric-label">Vendas Totais</span>
            <span className="metric-value">{metrics.totalSales}</span>
          </div>
          
          <div className="metric-item">
            <span className="metric-label">Investimento Total</span>
            <span className="metric-value">R$ {metrics.totalInvestment.toFixed(2)}</span>
          </div>
          
          <div className="metric-item">
            <span className="metric-label">Receita Total</span>
            <span className="metric-value">R$ {metrics.totalRevenue.toFixed(2)}</span>
          </div>
          
          <div className="metric-item">
            <span className="metric-label">ROI</span>
            <span className={`metric-value ${metrics.roi >= 0 ? 'positive' : 'negative'}`}>
              {metrics.roi.toFixed(2)}%
            </span>
          </div>
          
          <div className="metric-item">
            <span className="metric-label">Leads</span>
            <span className="metric-value">{metrics.totalLeads}</span>
          </div>
          
          <div className="metric-item">
            <span className="metric-label">Taxa de Conversão</span>
            <span className="metric-value">{metrics.conversionRate.toFixed(2)}%</span>
          </div>
        </div>
      </div>
      
      <div className="product-detail-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Visão Geral
        </button>
        <button 
          className={`tab ${activeTab === 'campaigns' ? 'active' : ''}`}
          onClick={() => setActiveTab('campaigns')}
        >
          Campanhas de Marketing
        </button>
        <button 
          className={`tab ${activeTab === 'metrics' ? 'active' : ''}`}
          onClick={() => setActiveTab('metrics')}
        >
          Métricas Detalhadas
        </button>
      </div>
      
      <div className="product-detail-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            <div className="charts-grid">
              <div className="chart-card">
                <h3>Investimento x Receita</h3>
                <div className="chart-container">
                  {investmentRevenueData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={investmentRevenueData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="investment" name="Investimento (R$)" fill="#8884d8" />
                        <Bar dataKey="revenue" name="Receita (R$)" fill="#82ca9d" />
                        <Bar dataKey="profit" name="Lucro (R$)" fill="#ff7300" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="no-data">Sem dados de campanhas para exibir gráficos.</p>
                  )}
                </div>
              </div>
              
              <div className="chart-card">
                <h3>Vendas por Período</h3>
                <div className="chart-container">
                  {salesData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={salesData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line type="monotone" dataKey="leads" name="Leads" stroke="#8884d8" activeDot={{ r: 8 }} />
                        <Line type="monotone" dataKey="sales" name="Vendas" stroke="#82ca9d" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="no-data">Sem dados de campanhas para exibir gráficos.</p>
                  )}
                </div>
              </div>
              
              <div className="chart-card">
                <h3>Distribuição de Investimento por Canal</h3>
                <div className="chart-container">
                  {channelData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={channelData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          nameKey="name"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {channelData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHANNEL_COLORS[index % CHANNEL_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `R$ ${value.toFixed(2)}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="no-data">Sem dados de canais para exibir o gráfico.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'campaigns' && (
          <div className="campaigns-section">
            {onStartAddCampaign && (
              <div className="campaigns-actions">
                <button className="btn-primary" onClick={onStartAddCampaign}>
                  Adicionar Nova Campanha
                </button>
              </div>
            )}
            
            {campaigns.length === 0 ? (
              <p className="no-data">
                Nenhuma campanha registrada para este produto.
                {onStartAddCampaign && " Adicione sua primeira campanha clicando no botão acima!"}
              </p>
            ) : (
              <div className="campaigns-table-container">
                <table className="campaigns-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Canal</th>
                      <th>Investimento</th>
                      <th>Leads</th>
                      <th>Vendas</th>
                      <th>Receita</th>
                      <th>ROI</th>
                      {onRemoveCampaign && <th>Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map(campaign => {
                      const roi = ((parseFloat(campaign.revenue) - parseFloat(campaign.investment)) / parseFloat(campaign.investment) * 100).toFixed(2);
                      
                      return (
                        <tr key={campaign.id} className={parseFloat(roi) >= 0 ? 'profitable' : 'not-profitable'}>
                          <td>{formatDate(campaign.date)}</td>
                          <td>
                            <span className={`channel-tag channel-${campaign.channel?.toLowerCase()}`}>
                              {campaign.channel}
                            </span>
                          </td>
                          <td>R$ {parseFloat(campaign.investment).toFixed(2)}</td>
                          <td>{campaign.leads}</td>
                          <td>{campaign.sales}</td>
                          <td>R$ {parseFloat(campaign.revenue).toFixed(2)}</td>
                          <td className={parseFloat(roi) >= 0 ? 'positive' : 'negative'}>
                            {roi}%
                          </td>
                          {onRemoveCampaign && (
                            <td className="action-buttons">
                              <button 
                                className="btn-delete" 
                                onClick={() => {
                                  if (window.confirm('Tem certeza que deseja remover esta campanha?')) {
                                    onRemoveCampaign(campaign.id);
                                  }
                                }}
                                title="Remover"
                              >
                                🗑️
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="2"><strong>TOTAL</strong></td>
                      <td>
                        <strong>
                          R$ {campaigns.reduce((sum, campaign) => sum + parseFloat(campaign.investment || 0), 0).toFixed(2)}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          {campaigns.reduce((sum, campaign) => sum + parseInt(campaign.leads || 0), 0)}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          {campaigns.reduce((sum, campaign) => sum + parseInt(campaign.sales || 0), 0)}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          R$ {campaigns.reduce((sum, campaign) => sum + parseFloat(campaign.revenue || 0), 0).toFixed(2)}
                        </strong>
                      </td>
                      <td colSpan={onRemoveCampaign ? 2 : 1}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'metrics' && (
          <div className="metrics-section">
            <div className="metrics-grid">
              <div className="metric-card">
                <h3>Desempenho de Vendas</h3>
                <div className="metric-details">
                  <div className="metric-row">
                    <span className="metric-label">Total de Vendas</span>
                    <span className="metric-value">{metrics.totalSales}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Total de Leads</span>
                    <span className="metric-value">{metrics.totalLeads}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Taxa de Conversão</span>
                    <span className="metric-value">{metrics.conversionRate.toFixed(2)}%</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Receita Média por Venda</span>
                    <span className="metric-value">
                      R$ {metrics.totalSales > 0 ? (metrics.totalRevenue / metrics.totalSales).toFixed(2) : '0.00'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="metric-card">
                <h3>Desempenho Financeiro</h3>
                <div className="metric-details">
                  <div className="metric-row">
                    <span className="metric-label">Investimento Total</span>
                    <span className="metric-value">R$ {metrics.totalInvestment.toFixed(2)}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Receita Total</span>
                    <span className="metric-value">R$ {metrics.totalRevenue.toFixed(2)}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Lucro</span>
                    <span className={`metric-value ${metrics.totalRevenue - metrics.totalInvestment >= 0 ? 'positive' : 'negative'}`}>
                      R$ {(metrics.totalRevenue - metrics.totalInvestment).toFixed(2)}
                    </span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">ROI</span>
                    <span className={`metric-value ${metrics.roi >= 0 ? 'positive' : 'negative'}`}>
                      {metrics.roi.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="metric-card">
                <h3>Custo de Aquisição</h3>
                <div className="metric-details">
                  <div className="metric-row">
                    <span className="metric-label">CPA (Custo por Aquisição)</span>
                    <span className="metric-value">R$ {metrics.cpa.toFixed(2)}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">CPL (Custo por Lead)</span>
                    <span className="metric-value">
                      R$ {metrics.totalLeads > 0 ? (metrics.totalInvestment / metrics.totalLeads).toFixed(2) : '0.00'}
                    </span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Valor do Cliente (LTV estimado)</span>
                    <span className="metric-value">
                      R$ {(parseFloat(product.price) * 1.5).toFixed(2)}
                    </span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Razão LTV:CAC</span>
                    <span className="metric-value">
                      {metrics.cpa > 0 ? ((parseFloat(product.price) * 1.5) / metrics.cpa).toFixed(2) : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="metrics-insights">
              <h3>Insights e Recomendações</h3>
              <div className="insights-content">
                {metrics.totalSales === 0 ? (
                  <p>Ainda não há dados suficientes para gerar insights. Adicione campanhas para este produto.</p>
                ) : (
                  <ul className="insights-list">
                    {metrics.roi < 0 && (
                      <li className="insight-item negative">
                        <strong>ROI Negativo:</strong> O retorno sobre o investimento está negativo. Considere revisar sua estratégia de marketing ou o preço do produto.
                      </li>
                    )}
                    
                    {metrics.roi > 200 && (
                      <li className="insight-item positive">
                        <strong>ROI Excelente:</strong> Seu ROI está acima de 200%. Este produto está performando muito bem, considere aumentar o investimento.
                      </li>
                    )}
                    
                    {metrics.conversionRate < 5 && metrics.totalLeads > 0 && (
                      <li className="insight-item negative">
                        <strong>Baixa Conversão:</strong> A taxa de conversão está abaixo de 5%. Avalie melhorias na página de vendas ou no funil de conversão.
                      </li>
                    )}
                    
                    {metrics.conversionRate > 20 && metrics.totalLeads > 10 && (
                      <li className="insight-item positive">
                        <strong>Alta Conversão:</strong> Taxa de conversão acima de 20% é excelente. Seu funil de vendas está funcionando bem.
                      </li>
                    )}
                    
                    {metrics.cpa > parseFloat(product.price) && (
                      <li className="insight-item negative">
                        <strong>CPA Alto:</strong> O custo de aquisição está acima do preço do produto. Isso pode indicar que suas campanhas não estão otimizadas.
                      </li>
                    )}
                    
                    {metrics.totalSales > 0 && metrics.totalLeads === 0 && (
                      <li className="insight-item warning">
                        <strong>Dados Inconsistentes:</strong> Há vendas registradas, mas nenhum lead. Verifique se os dados estão sendo registrados corretamente.
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;